'use client';
// ChartControls Component - Graph control buttons
import { useCallback } from 'react';
import CodeIcon from '@mui/icons-material/Code';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, IconButton, Tooltip } from '@mui/material';

interface ChartControlsProps {
  onFullscreen?: () => void;
  onDownload?: () => void;
  onEmbed?: () => void;
  onSettings?: () => void;
}

export function ChartControls({
  onFullscreen,
  onDownload,
  onEmbed,
  onSettings,
}: ChartControlsProps) {
  function handleFullscreen() {
    if (onFullscreen) {
      onFullscreen();
    } else {
      // Default: try to make chart container fullscreen
      const chartContainer = document.querySelector('[data-chart-container]');
      if (chartContainer && chartContainer.requestFullscreen) {
        chartContainer.requestFullscreen();
      }
    }
  }

  function handleDownload() {
    if (onDownload) {
      onDownload();
    } else {
      // Default: download chart as PNG
      const svg = document.querySelector('[data-chart-container] svg');
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
              if (blob) {
                const link = document.createElement('a');
                link.download = 'chart.png';
                link.href = URL.createObjectURL(blob);
                link.click();
                URL.revokeObjectURL(url);
              }
            });
          }
        };
        img.src = url;
      }
    }
  }

  function handleEmbed() {
    if (onEmbed) {
      onEmbed();
    } else {
      // Default: copy embed code to clipboard
      const embedCode = '<iframe src="..." width="800" height="400"></iframe>';
      void navigator.clipboard.writeText(embedCode);
    }
  }

  function handleSettings() {
    if (onSettings) {
      onSettings();
    }
    // Default: open settings modal (to be implemented)
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <Tooltip title="Fullscreen">
        <IconButton size="small" onClick={handleFullscreen} sx={{ p: 0.75 }}>
          <FullscreenIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download">
        <IconButton size="small" onClick={handleDownload} sx={{ p: 0.75 }}>
          <DownloadIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Embed">
        <IconButton size="small" onClick={handleEmbed} sx={{ p: 0.75 }}>
          <CodeIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Settings">
        <IconButton size="small" onClick={handleSettings} sx={{ p: 0.75 }}>
          <SettingsIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
